import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronDown, RefreshCw, Settings } from 'lucide-react';
import { useEvents, EventCard, EventDetailSheet } from '@/features/events';
import { useFilterStore, FilterSheet, getDateRangeFromPreset } from '@/features/filters';
import { useBrandsControllerGetBrands } from '@/generated/api/brands/brands';
import { haptic } from '@/shared/utils/telegram';
import type { EventLog, EventType } from '@/shared/types';

const EVENTS_PER_PAGE = 50;

const EVENT_TYPE_OPTIONS: EventType[] = ['QUALIFIED_CPA', 'FTD', 'REG', 'REDEP', 'CLICK']; // v2.0.0: CPA as first option

export function EventsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventLog | null>(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [page, setPage] = useState(0);
  const { brands, eventTypes, setBrands, setEventTypes, period, affiliateId, trackingTag, tagSender, tagGeo, tagSheetDate } = useFilterStore();

  // Fetch brands list for selector
  const { data: brandsData } = useBrandsControllerGetBrands();

  // Get date range from period
  const dateRange = period !== 'custom' ? getDateRangeFromPreset(period) : undefined;

  // Fetch events with filters from store and pagination
  const { data, isLoading, error, refetch } = useEvents({
    brand: brands.length > 0 ? brands[0] : undefined,
    event: eventTypes.length === 1 ? eventTypes[0] : undefined,
    fromDate: dateRange?.fromDate,
    toDate: dateRange?.toDate,
    affiliateId: affiliateId || undefined,
    trackingTag: trackingTag || undefined,
    limit: EVENTS_PER_PAGE,
    offset: page * EVENTS_PER_PAGE,
    sortOrder: 'desc' as const,
  } as any);

  // Filter events by search query
  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];

    const events = data.events as unknown as EventLog[];
    const query = searchQuery.toLowerCase();
    if (!query) return events;

    return events.filter(
      (ev) =>
        ev.brand?.toLowerCase().includes(query) ||
        ev.country?.toLowerCase().includes(query) ||
        ev.externalUid?.toLowerCase().includes(query) ||
        ev.source?.toLowerCase().includes(query),
    );
  }, [data?.events, searchQuery]);

  const handleEventClick = (event: EventLog) => {
    setSelectedEvent(event);
  };

  const handleFilterClick = () => {
    haptic.impact('medium');
    setFilterSheetOpen(true);
  };

  const handleRefresh = () => {
    haptic.impact('light');
    refetch();
  };

  const handleNextPage = () => {
    haptic.selection();
    setPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    haptic.selection();
    setPage((prev) => Math.max(0, prev - 1));
  };

  const handleBrandChange = (brand: string) => {
    haptic.impact('light');
    setBrands([brand]);
  };

  const handleEventTypeToggle = (eventType: EventType) => {
    haptic.selection();
    if (eventTypes.includes(eventType)) {
      setEventTypes(eventTypes.filter((t) => t !== eventType));
    } else {
      setEventTypes([eventType]); // Replace with single selected type
    }
  };

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [brands, eventTypes, period, affiliateId, trackingTag, tagSender, tagGeo, tagSheetDate]);

  const selectedBrand = brands.length > 0 ? brands[0] : null;
  const hasBrand = !!selectedBrand;

  return (
    <div className="flex flex-col h-full animate-in slide-in-from-right duration-300 pb-24">
      {/* Header & Filters */}
      <div className="sticky top-0 z-10 bg-[var(--tg-theme-bg-color)] pt-2 pb-4 flex flex-col gap-3">
        <div className="flex items-center justify-between px-1">
          <h1 className="text-2xl font-black uppercase tracking-tighter">Event Feed</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className={`p-2 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-link-color)] active:scale-90 transition-all ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleFilterClick}
              className="p-2 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-link-color)] active:scale-90 transition-transform relative"
            >
              <Filter size={18} />
              {period !== 'today' && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--tg-theme-link-color)] rounded-full" />
              )}
            </button>
            <button
              onClick={() => {
                haptic.impact('light');
                navigate('/settings');
              }}
              className="p-2 rounded-full hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors"
              aria-label="Settings"
            >
              <Settings size={20} className="text-[var(--tg-theme-hint-color)]" />
            </button>
          </div>
        </div>

        {/* Time Period Selector */}
        <div className="flex p-1 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl overflow-x-auto scrollbar-hide">
          {[
            { value: 'today' as const, label: '1D' },
            { value: 'yesterday' as const, label: 'YD' },
            { value: 'this_week' as const, label: '7D' },
            { value: 'this_month' as const, label: 'M' },
            { value: 'last_month' as const, label: 'LM' },
            { value: 'this_quarter' as const, label: 'Q' },
            { value: 'this_year' as const, label: 'YTD' },
          ].map((p) => (
            <button
              key={p.value}
              onClick={() => {
                haptic.selection();
                useFilterStore.getState().setPeriod(p.value);
              }}
              className={`flex-1 py-2.5 text-sm font-black rounded-lg transition-all min-w-[44px] ${
                period === p.value
                  ? 'bg-[var(--tg-theme-bg-color)] shadow-sm text-[var(--tg-theme-link-color)]'
                  : 'text-[var(--tg-theme-hint-color)]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Brand Selector */}
        {brandsData?.data?.brands && brandsData.data.brands.length > 0 && (
          <div className="relative">
            <select
              value={selectedBrand || ''}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full h-11 px-4 pr-10 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
            >
              <option value="">Select Brand...</option>
              {brandsData.data.brands.map((brand) => (
                <option key={brand.brand} value={brand.brand}>
                  {brand.brand} ({brand.totalEvents} events)
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <ChevronDown size={16} className="text-[var(--tg-theme-hint-color)]" />
            </div>
          </div>
        )}

        {hasBrand && (
          <>
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search size={14} className="text-[var(--tg-theme-hint-color)]" />
              </div>
              <input
                type="text"
                placeholder="Search ClickID, SubID, ExternalID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-10 pr-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20 placeholder:opacity-50"
              />
            </div>

            {/* Quick Event Type Filters */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {EVENT_TYPE_OPTIONS.map((type) => {
                const isActive = eventTypes.includes(type);
                const displayLabel = type === 'QUALIFIED_CPA' ? 'CPA' : type;
                return (
                  <button
                    key={type}
                    onClick={() => handleEventTypeToggle(type)}
                    className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap transition-all active:scale-95 ${
                      isActive
                        ? 'bg-[var(--tg-theme-link-color)] text-white'
                        : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
                    }`}
                  >
                    {displayLabel}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="p-4 pb-20">
        {/* No Brand Selected Placeholder */}
        {!hasBrand && (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-[var(--tg-theme-link-color)]/10 flex items-center justify-center">
              <Filter size={32} className="text-[var(--tg-theme-link-color)]" />
            </div>
            <div>
              <h3 className="text-lg font-black uppercase mb-2">Select a Brand</h3>
              <p className="text-sm text-[var(--tg-theme-hint-color)] font-medium">
                Choose a brand from the dropdown above or browse all brands
              </p>
            </div>
            <button
              onClick={() => navigate('/brands')}
              className="px-6 py-3 rounded-xl bg-[var(--tg-theme-link-color)] text-white font-bold text-sm active:scale-95 transition-transform"
            >
              Browse Brands
            </button>
          </div>
        )}

        {hasBrand && isLoading && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-8 h-8 border-4 border-[var(--tg-theme-link-color)] border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase">Loading events...</p>
          </div>
        )}

        {hasBrand && error && (
          <div className="flex flex-col items-center justify-center py-12 space-y-2">
            <p className="text-sm font-bold text-rose-600">Failed to load events</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Please try again later</p>
          </div>
        )}

        {hasBrand && filteredEvents.length === 0 && !isLoading && !error && (
          <div className="flex flex-col items-center justify-center py-12 space-y-2">
            <p className="text-sm font-bold text-[var(--tg-theme-hint-color)] uppercase">No events found</p>
            {searchQuery && <p className="text-xs text-[var(--tg-theme-hint-color)]">Try adjusting your search</p>}
          </div>
        )}

        {/* Events List */}
        {hasBrand && filteredEvents.length > 0 && (
          <>
            <div className="space-y-3">
              {filteredEvents.map((event) => (
                <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} />
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6 gap-4">
              <button
                onClick={handlePrevPage}
                disabled={page === 0}
                className="flex-1 py-2 px-4 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] font-bold text-sm active:scale-95 transition-transform border border-black/5 dark:border-white/5 disabled:opacity-30 disabled:active:scale-100"
              >
                Previous
              </button>
              <span className="text-sm font-bold text-[var(--tg-theme-hint-color)]">Page {page + 1}</span>
              <button
                onClick={handleNextPage}
                disabled={filteredEvents.length < EVENTS_PER_PAGE}
                className="flex-1 py-2 px-4 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] font-bold text-sm active:scale-95 transition-transform border border-black/5 dark:border-white/5 disabled:opacity-30 disabled:active:scale-100"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Event Detail Sheet */}
      <EventDetailSheet event={selectedEvent} open={!!selectedEvent} onClose={() => setSelectedEvent(null)} />

      {/* Filter Sheet */}
      <FilterSheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} />
    </div>
  );
}
